import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutStats } from '@/mocks/homeData';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Page Header */}
      <section className="w-full pt-28 md:pt-32 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
            About Us
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight">
            Our Mission
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
            We believe everyone deserves clean, beautiful beaches. Our platform connects generous subscribers with local coastal volunteer groups, turning $14 monthly donations into tangible shoreline cleanup action.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Small%20group%20of%20friends%20doing%20a%20beach%20cleanup%20on%20a%20sunny%20California%20beach%20at%20golden%20hour%2C%20carrying%20mesh%20bags%20filled%20with%20collected%20trash%2C%20Santa%20Monica%20pier%20in%20the%20distant%20background%2C%20warm%20nostalgic%20photography%2C%20genuine%20community%20bonding%20moment%2C%20organic%20natural%20lighting&width=800&height=600&seq=about-beach-origin&orientation=landscape"
                  alt="Beach cleanup origin story in Santa Monica"
                  className="w-full h-full object-cover object-top"
                  title="How Clear Beaches Started in Santa Monica"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-medium text-accent-600">
                Our Story
              </span>
              <h2 className="mt-2 text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
                How It Started
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                What began as a small beach cleanup in Santa Monica, California has grown into a nationwide coastal movement. In 2023, our founder organized the first shoreline cleanup with supplies bought out of pocket. The response was overwhelming — beachgoers wanted to keep going, but supplies ran out fast.
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                The subscription model was born from that simple problem: consistent funding means consistent beach cleanups. Today, thousands of subscribers fund kits for hundreds of beaches across the country, proving that small, recurring actions create massive coastal impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {aboutStats.map((stat) => (
              <div
                key={stat.id}
                className="text-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/60"
              >
                <span className="text-2xl md:text-4xl font-bold font-heading text-accent-600">
                  {stat.value}
                </span>
                <p className="mt-2 text-xs md:text-sm text-foreground-600">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-accent-700 bg-accent-100 rounded-full">
              Our Values
            </span>
            <h2 className="mt-4 text-2xl md:text-4xl font-bold font-heading text-foreground-950">
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            <div className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/60">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-eye-line text-lg md:text-xl text-primary-600"></i>
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2">
                Radical Transparency
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                Every dollar, every kit, every beach cleanup is tracked and reported. Subscribers can see exactly where their donations go and what impact they create. Zero percent of donations go to administrative overhead.
              </p>
            </div>

            <div className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/60">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                <i className="ri-anchor-line text-lg md:text-xl text-accent-600"></i>
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2">
                Local Coastlines First
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                Donations stay in the subscriber's coastal community. We prioritize volunteer groups that are based in and clean the same beaches where our subscribers live and swim.
              </p>
            </div>

            <div className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/60">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-secondary-100 rounded-lg mb-4">
                <i className="ri-hand-heart-line text-lg md:text-xl text-secondary-600"></i>
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2">
                Beach-Ready Equipment
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                Coastal cleanup demands specialized tools. Our kits use waterproof gloves, quick-drain mesh bags, microplastic sand sifters, and rust-resistant grabbers built specifically for shoreline conditions.
              </p>
            </div>

            <div className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/60">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-secondary-100 rounded-lg mb-4">
                <i className="ri-loop-left-line text-lg md:text-xl text-secondary-600"></i>
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2">
                Sustainable Action
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                Monthly subscriptions create reliable, predictable funding. Volunteer groups can plan ahead, schedule regular beach cleanups, and build momentum without worrying about where the next supplies will come from.
              </p>
            </div>

            <div className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/60">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-user-star-line text-lg md:text-xl text-primary-600"></i>
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2">
                Community Led
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                We do not tell communities what to do — we give them the tools and let them lead. Volunteer groups decide which beaches, when, and how to clean based on what their coastline actually needs.
              </p>
            </div>

            <div className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/60">
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                <i className="ri-global-line text-lg md:text-xl text-accent-600"></i>
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2">
                Scalable Impact
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                The model scales beautifully. More subscribers in a coastal city means more kits, more equipped groups, more frequent cleanups, and measurably cleaner shorelines for everyone to enjoy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100 text-center">
        <h3 className="text-xl md:text-3xl font-bold font-heading text-foreground-950">
          Want to bring cleanup kits to your local beaches?
        </h3>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-md mx-auto">
          It all starts with a $14 monthly donation. Your first kit is free, and every other month funds a kit for local beach volunteers.
        </p>
        <Link
          to="/donate"
          className="mt-6 inline-block px-8 py-3 md:px-10 md:py-3.5 text-sm md:text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          Start Your $14/month Donation
        </Link>
      </section>

      <Footer />
    </main>
  );
}